import { LocationType, type Stop } from '@/@types/gtfs-ressource';
import Common from '@/libs/common';

/**
 * Two namesakes this close are the two sides of one street, one stop_id per
 * direction. Measured on production data, the gain plateaus between 100 and
 * 250 m while real namesakes (« Stade », « Église »…) sit kilometres apart.
 */
export const STOP_CLUSTER_RADIUS_M = 150;

export interface StopClusterIndex {
  /** Canonical id -> every stop_id it stands for, itself included. */
  members: { [canonicalId: string]: Array<string> };
  /** Any stop_id -> the id displayed in its place. */
  canonicalOf: { [stopId: string]: string };
}

function distance(a: Stop, b: Stop): number {
  return Common.coordDistance(
    { latitude: a.stop_lat, longitude: a.stop_lon },
    { latitude: b.stop_lat, longitude: b.stop_lon },
  );
}

/** Connected components of the namesakes lying within the radius of one another. */
function clusterByDistance(namesakes: Array<Stop>): Array<Array<Stop>> {
  const root = namesakes.map((_, index) => index);
  const find = (index: number): number => {
    while (root[index] !== index) {
      root[index] = root[root[index]];
      index = root[index];
    }
    return index;
  };
  for (let i = 0; i < namesakes.length; i++) {
    for (let j = i + 1; j < namesakes.length; j++) {
      if (distance(namesakes[i], namesakes[j]) <= STOP_CLUSTER_RADIUS_M) root[find(i)] = find(j);
    }
  }

  const clusters = new Map<number, Array<Stop>>();
  namesakes.forEach((stop, index) => {
    const cluster = clusters.get(find(index)) ?? [];
    cluster.push(stop);
    clusters.set(find(index), cluster);
  });
  return [...clusters.values()];
}

/**
 * Groups the stop_ids a passenger sees as one stop. A declared `parent_station`
 * is trusted as is; the stops attached to none are merged by normalized name and
 * distance, onto their smallest stop_id — stable from one GTFS version to the
 * next, unlike a centroid.
 */
export function buildStopClusters(stops: { [stopId: string]: Stop }): StopClusterIndex {
  const canonicalOf: StopClusterIndex['canonicalOf'] = {};
  const unattachedByName = new Map<string, Array<Stop>>();

  Object.values(stops).forEach(stop => {
    if (stop.location_type === LocationType.STATION) {
      canonicalOf[stop.stop_id] = stop.stop_id;
    } else if (stop.parent_station) {
      canonicalOf[stop.stop_id] = stop.parent_station;
    } else {
      const name = Common.normalize(stop.stop_name ?? '');
      unattachedByName.set(name, [...(unattachedByName.get(name) ?? []), stop]);
    }
  });

  unattachedByName.forEach(namesakes =>
    clusterByDistance(namesakes).forEach(cluster => {
      const canonicalId = cluster
        .map(stop => stop.stop_id)
        .reduce((smallest, stopId) => (stopId < smallest ? stopId : smallest));
      cluster.forEach(stop => (canonicalOf[stop.stop_id] = canonicalId));
    }),
  );

  const members: StopClusterIndex['members'] = {};
  Object.entries(canonicalOf).forEach(([stopId, canonicalId]) => {
    (members[canonicalId] ??= []).push(stopId);
  });

  return { members, canonicalOf };
}

/** Accessible only when every stop of the group is: an unknown platform counts as not accessible. */
export function isStopGroupAccessible(stops: Array<Stop | undefined>): boolean {
  return stops.length > 0 && stops.every(stop => stop?.wheelchair_boarding === 1);
}

/** Explicitly not accessible only when every stop of the group says so: one accessible or unknown platform keeps the group neutral. */
export function isStopGroupNotAccessible(stops: Array<Stop | undefined>): boolean {
  return stops.length > 0 && stops.every(stop => stop?.wheelchair_boarding === 2);
}

export function isCanonicalStop(stopId: string, canonicalOf: StopClusterIndex['canonicalOf']): boolean {
  return (canonicalOf[stopId] ?? stopId) === stopId;
}

/**
 * Favorites are stored by stop_id: one set on a stop now merged into another
 * would vanish from the lists, which only show canonical stops. Ids unknown to
 * the GTFS are kept as is, they may come back with a later version.
 */
export function projectFavoriteStops(
  favoriteStops: { [stopId: string]: boolean },
  canonicalOf: StopClusterIndex['canonicalOf'],
): { [stopId: string]: boolean } {
  const projected: { [stopId: string]: boolean } = {};
  Object.entries(favoriteStops).forEach(([stopId, starred]) => {
    if (starred) projected[canonicalOf[stopId] ?? stopId] = true;
  });
  return projected;
}
